// PART_5B_AUTHSTORE_EXTENSION
// AuthStore - Zustand store for authentication state
// Part 5b: Extended with refresh-token rotation, silent refresh,
// multi-tab sync, and server-side logout.
//
// RBAC-HYDRATION FIX (accounting/fe-rbac-hydrate): setAuth and rehydrate now
// pull the effective permission set from /auth/me/permissions and populate the
// store, so usePermissions()-gated UI (e.g. Journal Entry post) unlocks after
// login and survives a page refresh. The fetch is fire-and-forget and never
// blocks auth; on failure it leaves existing perms untouched.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { jwtDecode } from 'jwt-decode'
import type { User } from '@/types'
import { authApi } from '@/api/auth'
import { __authInternals } from '@/lib/api'
// SECURITY (sec/s0f-token-storage): route persist through the single swappable
// token-storage backend instead of zustand's implicit localStorage default, so
// the storage layer lives in exactly one place. Behaviour is unchanged
// (localStorage today). See lib/tokenStorage.ts for the httpOnly-cookie TODO.
import { getPersistStorage } from '@/lib/tokenStorage'
// P2-1 (#1064): the channel moved to its own module so lib/api.ts can also
// broadcast LOGGED_OUT from the 401 forced-logout path without importing this
// store (which would be a cycle -- this store imports api.ts).
import {
  broadcastAuthEvent as broadcast,
  subscribeAuthEvents,
  subscribeTokenStorageSync,
  tokenSubject,
} from '@/lib/authBroadcast'
import { TOKEN_STORAGE_KEYS } from '@/lib/tokenStorage'
// PROD-SESSION-HARD-NAV-01 (F1.3): same reason as the channel above -- a
// standalone module both sides import, because lib/api.ts cannot import this
// store. Crucially it is dependency-free, so subscribing at MODULE SCOPE does
// not force '@/lib/api' to be real: the 42 test files that vi.mock('@/lib/api')
// without an `__authInternals` AND reach this store through
// tests/test-utils.tsx still load it.
import { registerAuthClearedSink, registerTokenSink } from '@/lib/authTokenSync'

export type CalendarPreference = 'gregorian' | 'hijri' | 'both'

/**
 * Lifecycle of the RBAC permission set (NAV-PERMS / B29 Lane A).
 *
 * `permissions: []` on its own is ambiguous -- it means BOTH "the fetch
 * hasn't landed yet" AND "this user genuinely holds nothing" (the
 * /auth/me/permissions contract returns [] for a user with no role_id).
 * A permission-filtered sidebar has to tell those apart: the first must
 * not render a nav at all, the second must render the un-gated nav.
 * This status is that discriminator.
 *
 *   idle    -- nothing attempted yet (logged out, or pre-hydration)
 *   loading -- a fetch is in flight and we hold no set yet
 *   ready   -- a fetch succeeded; `permissions` is authoritative
 *   error   -- the fetch failed and we hold no set -> consumers FAIL CLOSED
 *
 * Deliberately NOT persisted (see `partialize`): a persisted 'loading'
 * would strand the UI in a skeleton forever after a mid-flight reload.
 */
export type PermissionsStatus = 'idle' | 'loading' | 'ready' | 'error'

// ============================================================================
// Constants
// ============================================================================

const SILENT_REFRESH_LEAD_MS = 60_000        // refresh 1 min before exp

/**
 * Floor on how soon the silent-refresh timer may fire (PROD-SESSION-HARD-NAV-01).
 *
 * A token that arrives ALREADY inside the lead window used to be refreshed
 * synchronously (`void refreshFn()`), and the store's now-removed
 * MIN_REFRESH_INTERVAL_MS throttle was what stopped that from becoming a tight
 * loop: rotate -> new token still inside the window -> rotate again. The
 * concurrency gate is gone (there is one single-flight in lib/api.ts now), but
 * the runaway guard still has to exist, so it lives here as a delay floor
 * instead of a caller-side throttle. Nothing depends on the old immediate
 * fire any more -- the boot path awaits refresh() explicitly (F1.4).
 */
const MIN_SILENT_REFRESH_DELAY_MS = 5_000

// ============================================================================
// JWT helpers
// ============================================================================

interface JwtPayload {
  exp?: number
  sub?: string
  [key: string]: unknown
}

function getTokenExpiryMs(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = jwtDecode<JwtPayload>(token)
    if (typeof payload.exp === 'number') {
      return payload.exp * 1000  // exp is seconds since epoch
    }
  } catch {
    // Invalid token format - treat as expired
  }
  return null
}

/**
 * Is this token already inside the silent-refresh lead window?
 *
 * Used by onRehydrateStorage to decide between "refresh FIRST, then fetch
 * permissions" and "fetch permissions now, refresh later on a timer"
 * (PROD-SESSION-HARD-NAV-01 / F1.4). An undecodable token has no deadline to
 * be past, so it reads as not-due and is left to the request interceptor.
 */
function isTokenDueForRefresh(token: string | null): boolean {
  const expiryMs = getTokenExpiryMs(token)
  if (expiryMs === null) return false
  return expiryMs - SILENT_REFRESH_LEAD_MS - Date.now() <= 0
}

// ============================================================================
// Store definition
// ============================================================================

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean

  // S46: RBAC visibility
  permissions: string[]
  systemRole: string | null
  // NAV-PERMS: load lifecycle of `permissions` (see PermissionsStatus)
  permissionsStatus: PermissionsStatus

  // S49: tenant display preferences
  calendarPreference: CalendarPreference
  defaultCurrency: string

  // Part 5b: refresh state
  isRefreshing: boolean
  lastRefreshAt: number  // timestamp ms; 0 means never

  // Actions
  setAuth: (user: User, token: string, refreshToken?: string | null) => void
  logout: () => Promise<void>
  logoutAll: () => Promise<void>
  refresh: () => Promise<string | null>
  updateUser: (user: User) => void
  setPermissions: (permissions: string[], systemRole: string | null) => void
  hydratePermissions: () => Promise<void>
  setTenantPrefs: (
    calendarPreference: CalendarPreference,
    defaultCurrency: string,
  ) => void
}

// ============================================================================
// Silent refresh timer (singleton, lives outside store)
// ============================================================================

let silentRefreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * How many times in a row the timer may fire on the DELAY FLOOR before the
 * store stops arming it (#1081 P3).
 *
 * A clamped fire means the token we were just handed is already inside the
 * lead window, so refreshing it produces another token that is also already
 * inside the lead window: rotate, clamp, rotate, clamp -- a slow loop that
 * burns a refresh-token generation every MIN_SILENT_REFRESH_DELAY_MS for as
 * long as the tab is open. The floor keeps it slow; this cap ends it. After
 * the cap the store simply stops pre-empting, and the next real 401 goes
 * through the interceptor, which owns the force-logout decision.
 */
const MAX_CONSECUTIVE_IMMEDIATE_REFRESHES = 3
let consecutiveImmediateRefreshes = 0

function clearSilentRefresh(): void {
  if (silentRefreshTimer !== null) {
    clearTimeout(silentRefreshTimer)
    silentRefreshTimer = null
  }
}

/** A new session starts with a clean re-fire budget. */
function resetSilentRefreshBackoff(): void {
  consecutiveImmediateRefreshes = 0
}

function scheduleSilentRefresh(
  token: string | null,
  refreshFn: () => Promise<string | null>,
): void {
  clearSilentRefresh()
  if (!token) return

  const expiryMs = getTokenExpiryMs(token)
  if (expiryMs === null) return

  const naturalDelay = expiryMs - SILENT_REFRESH_LEAD_MS - Date.now()

  if (naturalDelay > MIN_SILENT_REFRESH_DELAY_MS) {
    // A token with life left in it: normal scheduling, and the re-fire budget
    // is restored because the rotation clearly produced a usable token.
    resetSilentRefreshBackoff()
    silentRefreshTimer = setTimeout(() => {
      void refreshFn()
    }, naturalDelay)
    return
  }

  // Already expired or inside the lead window -> refresh as soon as the floor
  // allows, rather than synchronously (see MIN_SILENT_REFRESH_DELAY_MS), and
  // only while there is budget left (MAX_CONSECUTIVE_IMMEDIATE_REFRESHES).
  consecutiveImmediateRefreshes += 1
  if (consecutiveImmediateRefreshes > MAX_CONSECUTIVE_IMMEDIATE_REFRESHES) return

  silentRefreshTimer = setTimeout(() => {
    void refreshFn()
  }, MIN_SILENT_REFRESH_DELAY_MS)
}

// ============================================================================
// Supersede-by-sequence (review #1081 R3)
// ----------------------------------------------------------------------------
// One counter per KIND of fire-and-forget fetch. Each call takes the next
// number; a call whose number is no longer the latest knows a newer call of
// the same kind has started and silently drops its result.
//
// It deliberately does NOT track the token. A token change and "a replacement
// fetch has started" are different events -- most rotations are the former
// without the latter -- and only the latter means someone else is going to
// write the answer.
// ============================================================================

let permissionsFetchSeq = 0
let identityFetchSeq = 0

/**
 * Advanced every time this context's credentials are torn down (review #1081
 * R4).
 *
 * The sequence counters above answer "did a newer fetch of my kind start?",
 * which a LOGOUT never does -- it starts nothing. That left a real hole:
 * `logout()` awaits POST /auth/logout before clearing state, so a
 * GET /auth/me/permissions already in flight could resolve afterwards and
 * write the departed user's set back into a logged-out store. `partialize`
 * only drops `permissionsStatus`, so the set was then PERSISTED to
 * `auth-storage` -- and the next person to sign in on that browser inherited
 * it: setAuth kept it, `hadSet` read true, the status went straight to
 * 'ready', and a viewer was gated as the previous admin. It survived a browser
 * restart, because it lived in localStorage.
 *
 * So the guard is two-part: my fetch is still the current one AND the session
 * it belongs to has not ended.
 */
let logoutEpoch = 0

/**
 * Called wherever auth is torn down -- BEFORE any await, so a response that
 * lands during the teardown is already too late.
 */
function bumpLogoutEpoch(): void {
  logoutEpoch += 1
}

/** Test-only: drop the counters and the epoch between cases. */
export function __resetFetchSequencesForTests(): void {
  permissionsFetchSeq = 0
  identityFetchSeq = 0
  logoutEpoch = 0
}

// ============================================================================
// Permission hydration (RBAC-HYDRATION FIX)
// ----------------------------------------------------------------------------
// Pull the effective permission set from the backend and populate the store.
// Defined at module scope (not a hook) so it can be called from setAuth and
// onRehydrateStorage without importing usePermissions. Fire-and-forget: never
// throws, never clears existing perms on failure.
// ============================================================================

async function loadPermissionsInto(
  set: (partial: Partial<AuthState>) => void,
  get: () => AuthState,
): Promise<void> {
  // "Has a NEWER permissions fetch started since mine?" -- nothing else.
  //
  // Two different-user TOKENS_UPDATED events in quick succession leave two
  // fetches in flight, and the network decides which lands last, not us.
  // Writing a late answer would put the FIRST user's permission set under the
  // SECOND user's token, the exact failure #1064 P2-1 set out to prevent
  // (review #1081 R2/F2).
  //
  // R3 FIX -- this guard first compared the store's TOKEN against the one
  // captured before the await, which was wrong because it conflated two
  // different things. Plenty of rotations change the token WITHOUT starting a
  // replacement fetch: the interceptor's 401-retry and the silent refresh both
  // land through registerTokenSink, and a cross-tab TOKENS_UPDATED for the
  // SAME user takes the sameUser branch that deliberately does not re-fetch.
  // In all of those the in-flight response is the only one coming, so
  // discarding it stranded the store on `permissionsStatus: 'loading'` with an
  // empty set forever -- worst on a cold set (first login, or an empty
  // persisted one), where nothing later would have repaired it.
  //
  // A counter says exactly what we mean, and it is only ever bumped where a
  // real replacement fetch begins.
  const mySeq = ++permissionsFetchSeq
  const myEpoch = logoutEpoch
  // Two questions, and both must still answer yes before we write: has no
  // newer fetch of this kind started (R3), and is the session this answer
  // belongs to still the session we are in (R4)?
  const stillOurs = () => mySeq === permissionsFetchSeq && myEpoch === logoutEpoch

  // A set already in hand (persisted from a previous session, or an earlier
  // successful fetch) means this call is a re-sync, not a cold load: stay
  // 'ready' so permission-gated UI doesn't flash a skeleton on every reload.
  const hadSet = get().permissions.length > 0
  set({ permissionsStatus: hadSet ? 'ready' : 'loading' })
  try {
    const resp = await authApi.myPermissions()
    if (!stillOurs()) return
    set({
      permissions: resp.permissions ?? [],
      systemRole: resp.role ?? null,
      permissionsStatus: 'ready',
    })
    if (resp.tenant_calendar_preference || resp.tenant_default_currency) {
      set({
        calendarPreference: resp.tenant_calendar_preference || 'gregorian',
        defaultCurrency: resp.tenant_default_currency || 'AED',
      })
    }
  } catch {
    // Superseded, or the session ended: the status belongs to whatever fetch
    // IS current, so even the error write has to be skipped -- it would
    // otherwise knock a live 'loading' down to 'error' on the newer fetch's
    // behalf, or put a status back on a logged-out store.
    if (!stillOurs()) return
    // Endpoint unreachable / transient failure: keep existing perms in place
    // rather than clearing them (avoids a flicker-to-locked on a blip).
    // With nothing in hand there is no set to keep -> 'error', and consumers
    // that filter on permissions must fail CLOSED rather than show everything.
    set({ permissionsStatus: hadSet ? 'ready' : 'error' })
  }
}

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      permissions: [],
      systemRole: null,
      permissionsStatus: 'idle',
      calendarPreference: 'gregorian',
      defaultCurrency: 'AED',
      isRefreshing: false,
      lastRefreshAt: 0,

      setAuth: (user, token, refreshToken = null) => {
        // Whose set is currently in the store? A DIFFERENT person signing in
        // must never inherit it (review #1081 R4). The set is persisted, so
        // without this the previous user's permissions survive a logout, a new
        // login and even a browser restart -- and because a non-empty set on
        // its own reads as authoritative (permissionsReady), the newcomer is
        // gated as them until the fetch below lands. Fail closed: start empty
        // and 'loading' so permission-gated UI stays shut in the meantime.
        //
        // A re-auth for the SAME person (MFA completion, flows that call
        // setAuth again) keeps the set, which is what stops the sidebar
        // flashing its skeleton on every one of them.
        const previousUser = get().user
        const sameUser =
          previousUser != null && user != null && previousUser.id === user.id

        // Mirror tokens into localStorage so the axios interceptor
        // (which reads from localStorage directly) sees them.
        __authInternals.setTokens(token, refreshToken)
        set({
          user,
          token,
          refreshToken,
          isAuthenticated: true,
          ...(sameUser
            ? {}
            : {
                permissions: [],
                systemRole: null,
                permissionsStatus: 'loading' as const,
              }),
        })
        broadcast({
          type: 'TOKENS_UPDATED',
          accessToken: token,
          refreshToken,
          sub: tokenSubject(token),
        })
        resetSilentRefreshBackoff()
        scheduleSilentRefresh(token, get().refresh)
        // RBAC-HYDRATION FIX: the login/MFA/signup responses carry the user +
        // token but NOT the permission set. Pull it now so usePermissions()
        // gates (e.g. Journal Entry post) unlock without a manual reload.
        // Fire-and-forget so setAuth stays synchronous for its callers.
        void loadPermissionsInto(set, get)
      },

      logout: async () => {
        // FIRST, before the network call below: this session is over as far as
        // any in-flight permission/identity fetch is concerned, and POST
        // /auth/logout is a round-trip one of them can easily outlive
        // (review #1081 R4).
        bumpLogoutEpoch()
        clearSilentRefresh()
        resetSilentRefreshBackoff()
        const refreshToken = get().refreshToken
        // Best-effort server-side revocation (don't block on failure)
        if (refreshToken) {
          try {
            await authApi.logout({ refresh_token: refreshToken })
          } catch {
            /* network errors - still proceed with local logout */
          }
        }
        __authInternals.clearAllAuth()
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          permissions: [],
          systemRole: null,
          permissionsStatus: 'idle',
          calendarPreference: 'gregorian',
          defaultCurrency: 'AED',
          isRefreshing: false,
          lastRefreshAt: 0,
        })
        broadcast({ type: 'LOGGED_OUT' })
      },

      logoutAll: async () => {
        bumpLogoutEpoch()
        clearSilentRefresh()
        resetSilentRefreshBackoff()
        try {
          await authApi.logoutAll()
        } catch {
          /* still proceed with local logout */
        }
        __authInternals.clearAllAuth()
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          permissions: [],
          systemRole: null,
          permissionsStatus: 'idle',
          calendarPreference: 'gregorian',
          defaultCurrency: 'AED',
          isRefreshing: false,
          lastRefreshAt: 0,
        })
        broadcast({ type: 'LOGGED_OUT' })
      },

      refresh: async () => {
        // PROD-SESSION-HARD-NAV-01 (F1.2): this used to be a SECOND refresh
        // implementation with its own `isRefreshing` / `lastRefreshAt` gate,
        // calling authApi.refresh with `_skipAuthRefresh` so it would bypass
        // the interceptor's queue. Two locks meant no lock: the store's silent
        // refresh and the interceptor's 401 refresh could hit POST
        // /auth/refresh concurrently with the same single-use token, and the
        // loser's 401 force-logged-out a live session.
        //
        // There is now exactly one implementation, in lib/api.ts. The state
        // write, the broadcast and the timer re-arm all happen in the token
        // listener registered below, for the interceptor's rotations too --
        // so the store can never again drift from the raw localStorage keys.
        //
        // The refresh token is read from storage rather than from state: an
        // interceptor-side rotation may have advanced it since this store last
        // set it, and storage is the copy the request actually carries.
        if (!__authInternals.getRefreshToken() && !get().refreshToken) {
          return null
        }

        set({ isRefreshing: true })
        try {
          return await __authInternals.refreshAccessToken()
        } catch {
          // Force-logout is the INTERCEPTOR's call, not ours: a failed silent
          // refresh must not eject a user whose access token is still good.
          // When the failure matters, the next 401 routes through lib/api.ts.
          return null
        } finally {
          set({ isRefreshing: false })
        }
      },

      updateUser: (user) => {
        set({ user })
      },

      setPermissions: (permissions, systemRole) => {
        // An explicit caller-supplied set is authoritative: mark it 'ready' so
        // permission-gated UI stops waiting (NAV-PERMS).
        set({ permissions, systemRole, permissionsStatus: 'ready' })
      },

      hydratePermissions: async () => {
        await loadPermissionsInto(set, get)
      },

      setTenantPrefs: (calendarPreference, defaultCurrency) => {
        set({ calendarPreference, defaultCurrency })
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => getPersistStorage()),
      // NAV-PERMS: persist everything EXCEPT permissionsStatus. A persisted
      // 'loading' (tab closed mid-fetch) would rehydrate as a permanent
      // skeleton; the status is always re-derived from the fresh fetch.
      partialize: (state) => {
        const persisted = { ...state }
        delete (persisted as Partial<AuthState>).permissionsStatus
        return persisted as AuthState
      },
      onRehydrateStorage: () => (state) => {
        // The one code path that runs on a FULL page load and never on SPA
        // navigation -- which is exactly why PROD-SESSION-HARD-NAV-01 only
        // reproduced on hard loads.
        //
        // It used to fire both of the below in parallel. With a token at or
        // past its lead window that meant two concurrent rotations of the same
        // single-use refresh token (the timer's immediate fire, and the 401 on
        // the permissions fetch), and the loser's 401 bounced the user to
        // /login. They are now ordered: refresh first, hydrate with the token
        // that came back. A token with life left in it skips the wait
        // entirely and hydrates immediately -- no boot-time refresh at all
        // (F1.4).
        if (!state || !state.token) return

        if (!isTokenDueForRefresh(state.token)) {
          scheduleSilentRefresh(state.token, state.refresh)
          // RBAC-HYDRATION FIX: re-pull permissions after a page refresh so
          // usePermissions() gates stay unlocked across reloads (the perms
          // array is persisted too, but this re-syncs against any change).
          void state.hydratePermissions()
          return
        }

        void (async () => {
          // Resolves either way; a failed rotation returns null and the
          // hydrate below then 401s into the interceptor, which owns the
          // force-logout decision. The timer is re-armed by the token
          // listener when the rotation succeeds.
          await state.refresh()
          await state.hydratePermissions()
        })()
      },
    },
  ),
)

// ============================================================================
// Same-tab token sync (PROD-SESSION-HARD-NAV-01 / F1.3)
// ----------------------------------------------------------------------------
// lib/api.ts publishes here on EVERY successful rotation, whichever caller
// asked for it, immediately after writing the raw localStorage keys. It is a
// direct call and not a broadcast on purpose: BroadcastChannel never delivers
// to the context that posted, so the refreshing tab would otherwise be the one
// tab that never learns its own new token -- which is precisely how
// `auth-storage` drifted away from `access_token` before this existed.
//
// Registered unconditionally at module scope so a rotation triggered before
// any component mounts (the boot refresh in onRehydrateStorage) is still
// mirrored into the store. That is why the registry is its own dependency-free
// module and not part of `__authInternals`: module-scope code here must not
// require '@/lib/api' to be the real thing.
// ============================================================================

// The forced logout in lib/api.ts (a refresh this tab cannot recover from)
// tears auth down without going through any of the store actions above, and a
// tab never receives its own LOGGED_OUT broadcast -- so it is announced
// through the same dependency-free registry as a rotation (review #1081 R4).
registerAuthClearedSink(bumpLogoutEpoch)

registerTokenSink(({ accessToken, refreshToken }) => {
  useAuthStore.setState((prev) => ({
    token: accessToken,
    // The backend always returns a rotated refresh token, but a response
    // without one must not blank the working one we still hold.
    refreshToken: refreshToken ?? prev.refreshToken,
    lastRefreshAt: Date.now(),
  }))
  scheduleSilentRefresh(accessToken, useAuthStore.getState().refresh)
})

// ============================================================================
// Multi-tab sync: react to BroadcastChannel messages from other tabs
// ============================================================================

/**
 * Re-derive `user` from whatever token this tab now holds (#1081 P3).
 *
 * Called when a TOKENS_UPDATED arrives whose `sub` is NOT the subject this tab
 * was showing -- another tab signed a different person in. The token is
 * adopted (it is the live credential for this origin), so the displayed
 * identity has to follow it rather than stay on the previous user.
 *
 * Never throws. A failure leaves `user` null, which reads as "not known yet"
 * rather than as the wrong person; the request that matters next will 401 into
 * the interceptor, which owns the force-logout decision.
 */
async function adoptIdentityFromToken(): Promise<void> {
  // Same supersede-by-sequence rule as loadPermissionsInto, for the same
  // reason and with the same R3 correction: two different-user TOKENS_UPDATED
  // events back to back leave two GET /auth/me calls in flight, and if the
  // FIRST resolves last it writes the FIRST user into a store that has already
  // moved on -- but a rotation for the user we just adopted starts NO second
  // /auth/me, so keying the guard on the token left `user` null for good.
  const mySeq = ++identityFetchSeq
  const myEpoch = logoutEpoch
  try {
    const user = await authApi.me()
    // Superseded by a newer /auth/me (R3), or the session ended while this one
    // was in flight (R4). The epoch replaces an earlier `if (!token) return`,
    // which only caught a teardown that had already finished writing -- and
    // which had no counterpart at all on the permissions side.
    if (mySeq !== identityFetchSeq || myEpoch !== logoutEpoch) return
    useAuthStore.setState({ user, isAuthenticated: true })
  } catch {
    /* fail closed: no identity is better than the previous user's */
  }
}

if (typeof window !== 'undefined') {
  subscribeAuthEvents((event) => {
    const store = useAuthStore.getState()

    if (event.type === 'LOGGED_OUT') {
      bumpLogoutEpoch()
      clearSilentRefresh()
      resetSilentRefreshBackoff()
      __authInternals.clearAllAuth()
      useAuthStore.setState({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        permissions: [],
        systemRole: null,
        permissionsStatus: 'idle',
        calendarPreference: 'gregorian',
        defaultCurrency: 'AED',
        isRefreshing: false,
        lastRefreshAt: 0,
      })
    } else if (event.type === 'TOKENS_UPDATED') {
      __authInternals.setTokens(event.accessToken, event.refreshToken)

      // P2-1 (#1064): adopting a token from another tab USED to write only the
      // tokens. If that other tab had logged in as a different user, this tab
      // kept user A's `permissions` while sending user B's token -- and because
      // a non-empty set reads as authoritative (permissionsReady), the stale
      // set would have gone on filtering the nav indefinitely. So the set was
      // dropped on every TOKENS_UPDATED and re-fetched.
      //
      // That blanket drop was the deliberate cost of a broadcast that carried
      // no identity, and #1064 named comparing the JWT `sub` as the follow-up.
      // It is now required rather than nice-to-have: PROD-SESSION-HARD-NAV-01
      // routes EVERY rotation through this broadcast (the interceptor's too),
      // so dropping the set unconditionally would flash the sidebar skeleton
      // in every other tab on every rotation -- TOKEN-REFRESH-FLICKER-01.
      //
      // So: drop the set only when the identity actually changed. `sub` absent
      // (a tab still on the older bundle) reads as unknown and keeps the old,
      // safe behaviour.
      const nextSub = event.sub ?? null
      const currentSub = tokenSubject(store.token)
      const sameUser = nextSub !== null && currentSub !== null && nextSub === currentSub

      useAuthStore.setState(
        sameUser
          ? {
              token: event.accessToken,
              refreshToken: event.refreshToken ?? store.refreshToken,
              lastRefreshAt: Date.now(),
            }
          : {
              token: event.accessToken,
              // `?? store.refreshToken` on BOTH branches (#1081 P3). A missing
              // refresh token in the event must not blank the store's, because
              // the setTokens() call above does not blank STORAGE's either
              // (tokenStorage.setTokens ignores a null refresh token). Nulling
              // only the store would re-open exactly the mirror-vs-raw-keys
              // drift that F1.3 closed.
              refreshToken: event.refreshToken ?? store.refreshToken,
              // A DIFFERENT user is behind this token, so everything this tab
              // shows about "who is signed in" belongs to the previous one
              // (#1081 P3). Identity is dropped along with the permission set
              // and re-derived from the adopted token below -- otherwise the
              // header/profile keep rendering user A while every request goes
              // out as user B.
              user: null,
              permissions: [],
              systemRole: null,
              permissionsStatus: 'idle',
              lastRefreshAt: Date.now(),
            },
      )
      scheduleSilentRefresh(event.accessToken, useAuthStore.getState().refresh)

      if (!sameUser) {
        // Re-fetch against the adopted token. Both are fire-and-forget and
        // neither throws; on failure the permission status lands on 'error'
        // and consumers fail closed.
        //
        // Started in PARALLEL, not chained: hydratePermissions() flips the
        // status to 'loading' synchronously, which is what makes the empty set
        // read as "not loaded yet" rather than "this user holds nothing" for
        // the whole window. Waiting on /auth/me first would leave a gap where
        // the status is still 'idle'.
        void useAuthStore.getState().hydratePermissions()
        void adoptIdentityFromToken()
      }
    }
  })

  // SESSION-LOGOUT-WHILE-POLLING-01 / P2-3 -- the same adoption, for browsers
  // that have no BroadcastChannel.
  //
  // `subscribeAuthEvents` is a no-op there, so those tabs heard nothing when
  // another tab rotated: storage was correct, this store was a generation
  // behind, and only a reload fixed it. `storage` events fire in every OTHER
  // tab and never in the writer, which is the semantics we want.
  //
  // It reuses the TOKENS_UPDATED path above by constructing the same event, so
  // the `sub`-comparison, the permission-set rules and the silent-refresh
  // re-arm are identical on both transports -- one behaviour, two carriers.
  // `subscribeTokenStorageSync` is inert when the channel exists, so no browser
  // ever gets both for the same rotation, and the handler never rebroadcasts.
  //
  // READ-ONLY, DELIBERATELY (round-2 review P2). This used to open with
  // `__authInternals.setTokens(accessToken, refreshToken)` -- a WRITE, in answer
  // to a notification that storage had been written. Two things followed:
  //
  //   1. an echo. A `storage` event fires in every tab but the writer, so each
  //      tab answering one became a writer, waking all the others. Two tabs was
  //      enough to keep handing the event back and forth, re-arming the
  //      silent-refresh timer and re-deriving identity on every lap;
  //   2. a DOWNGRADE. The pair used to be assembled from the event's captured
  //      value plus storage's current one, so a late gen-1 event wrote gen-1's
  //      access token over the gen-2 one already in storage -- corrupting the
  //      shared keys that every request reads from, in the name of syncing them.
  //
  // The writer already put the pair in storage, and storage is shared: there is
  // nothing for a reader to write. `subscribeTokenStorageSync` now hands over the
  // pair as it stands at delivery time, and this handler updates LOCAL sinks only
  // -- the zustand state, the silent-refresh timer, the identity/permission
  // re-derivation. Nothing here touches localStorage or any channel.
  subscribeTokenStorageSync(
    ({ accessToken, refreshToken }) => {
      const store = useAuthStore.getState()
      if (store.token === accessToken) return   // already adopted; nothing new

      const nextSub = tokenSubject(accessToken)
      const currentSub = tokenSubject(store.token)
      const sameUser =
        nextSub !== null && currentSub !== null && nextSub === currentSub

      useAuthStore.setState(
        sameUser
          ? {
              token: accessToken,
              refreshToken: refreshToken ?? store.refreshToken,
              lastRefreshAt: Date.now(),
            }
          : {
              token: accessToken,
              refreshToken: refreshToken ?? store.refreshToken,
              user: null,
              permissions: [],
              systemRole: null,
              permissionsStatus: 'idle',
              lastRefreshAt: Date.now(),
            },
      )
      scheduleSilentRefresh(accessToken, useAuthStore.getState().refresh)

      if (!sameUser) {
        void useAuthStore.getState().hydratePermissions()
        void adoptIdentityFromToken()
      }
    },
    { access: TOKEN_STORAGE_KEYS.ACCESS_TOKEN, refresh: TOKEN_STORAGE_KEYS.REFRESH_TOKEN },
  )
}